import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, X, ArrowRight, ArrowLeft, Check, ChevronDown, FileCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtUSD, FLAGS, COUNTRIES } from "@/lib/utils";
import DevStatusBadge, { DEV_STATUS_OPTIONS } from "@/components/DevStatusBadge";
import ImageUploader, { SecureImage } from "@/components/ImageUploader";
import ReviewStatusBadge from "@/components/ReviewStatusBadge";
import DevelopmentDocumentsModal, { DevelopmentDocumentsPanel } from "@/components/DevelopmentDocuments";
import { formatApiError } from "@/lib/api";

const TAGS = ["Off-plan", "Ready", "Luxury", "Waterfront", "Smart Home", "Gated", "High-rise", "Premium", "Rental Ready"];
const AMENITIES = ["Swimming Pool", "Gym & Fitness Center", "24/7 Security", "Underground Parking", "Rooftop Lounge", "Children's Play Area", "Tennis Court", "Clubhouse", "Concierge", "EV Charging"];

export default function DeveloperProperties() {
  const [devs, setDevs] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [docsDev, setDocsDev] = useState(null);

  async function load() {
    const { data } = await api.get("/developer/dashboard");
    setDevs(data.developments_list || []);
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Properties</p>
          <h1 className="text-4xl font-bold text-neutral-900 mt-2">Your developments</h1>
          <p className="text-neutral-500 mt-2">Manage listings, units, payment plans &amp; media.</p>
        </div>
        <button data-testid="open-add-development" onClick={() => setShowWizard(true)} className="inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold transition glow-primary">
          <Plus size={16} /> Add Development
        </button>
      </div>

      {devs.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-neutral-200 bg-white p-12 text-center mt-10">
          <p className="text-lg font-semibold text-neutral-900">No properties listed yet</p>
          <p className="text-sm text-neutral-500 mt-1">Add your first development to start attracting investors.</p>
          <button data-testid="empty-add-development" onClick={() => setShowWizard(true)} className="mt-5 inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm">
            <Plus size={14} /> Add Development
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {devs.map((d) => {
            return (
              <div key={d.id} className="soft-card overflow-hidden block soft-card-hover relative">
                <Link to={`/development/${d.id}`} className="block">
                  <span data-testid={`dev-review-${d.id}`} className="absolute top-3 left-3 z-10"><ReviewStatusBadge status={d.review_status} /></span>
                  <SecureImage src={d.cover_image} alt="" className="w-full h-44 object-cover" />
                  <div className="p-5 pb-3">
                    <p className="font-semibold text-neutral-900">{d.name}</p>
                    <p className="text-xs text-neutral-500 flex items-center gap-1 mt-1"><MapPin size={12} /> {d.city}, {d.country}</p>
                    <p className="text-lg font-bold text-propera tabular mt-3">{fmtUSD(d.starting_price)}</p>
                  </div>
                </Link>
                <div className="px-5 pb-5 space-y-3">
                  <button data-testid={`dev-docs-open-${d.id}`} onClick={() => setDocsDev(d)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-200 px-3 py-2 text-xs font-semibold text-neutral-700 hover:border-propera hover:text-propera transition-colors">
                    <FileCheck size={14} /> Documents &amp; verification
                  </button>
                  <DevStatusEditor
                    devId={d.id}
                    current={d.status || "coming_soon"}
                    onChanged={load}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {docsDev && <DevelopmentDocumentsModal dev={docsDev} onClose={() => { setDocsDev(null); load(); }} />}
      {showWizard && <AddDevelopmentWizard onClose={() => { setShowWizard(false); load(); }} />}
    </div>
  );
}

function DevStatusEditor({ devId, current, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  async function change(next) {
    if (next === current) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch(`/developments/${devId}/status`, { status: next });
      toast.success("Development status updated");
      setOpen(false);
      onChanged && onChanged();
    } catch {
      toast.error("Could not update status");
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="relative" data-testid={`dev-status-editor-${devId}`}>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1.5">Construction Status</p>
      <button
        type="button"
        data-testid={`dev-status-btn-${devId}`}
        disabled={saving}
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-between gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 hover:border-neutral-300"
      >
        <DevStatusBadge status={current} />
        <ChevronDown size={14} className="text-neutral-400" />
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-2 rounded-2xl border border-neutral-200 bg-white shadow-lg p-1">
          {DEV_STATUS_OPTIONS.map((o) => (
            <button
              key={o.key}
              data-testid={`dev-status-option-${devId}-${o.key}`}
              onClick={() => change(o.key)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-xl hover:bg-neutral-50 text-sm"
            >
              <DevStatusBadge status={o.key} />
              {o.key === current && <Check size={14} className="text-propera" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddDevelopmentWizard({ onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", country: "Nigeria", city: "", description: "",
    completion_date: "", cover_image: "", images: [],
    tags: [], selling_points: [""], amenities: [],
    starting_price: "", payment_plan: { booking: 10, construction: 20, handover: 70 },
    units: [{ label: "Unit A", floor: "1", bedrooms: "Studio", price: "", deposit: "", estimated_rent: "" }],
  });
  const [createdDev, setCreatedDev] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function toggleArr(key, val) {
    setForm((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  }

  function addSellingPoint() { set("selling_points", [...form.selling_points, ""]); }
  function setSP(i, v) {
    const arr = [...form.selling_points];
    arr[i] = v;
    set("selling_points", arr);
  }
  function addUnit() {
    set("units", [...form.units, { label: `Unit ${String.fromCharCode(65 + form.units.length)}`, floor: "1", bedrooms: "Studio", price: "", deposit: "", estimated_rent: "" }]);
  }
  function setUnit(i, k, v) {
    const arr = [...form.units];
    arr[i] = { ...arr[i], [k]: v };
    set("units", arr);
  }
  function removeUnit(i) {
    set("units", form.units.filter((_, idx) => idx !== i));
  }

  async function submit() {
    setSubmitting(true);
    try {
      const fallback = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1600&q=80";
      const images = (form.images && form.images.length > 0) ? form.images : (form.cover_image ? [form.cover_image] : []);
      const cover = images[0] || fallback;
      const payload = {
        name: form.name,
        country: form.country,
        city: form.city,
        description: form.description,
        completion_date: form.completion_date,
        cover_image: cover,
        gallery: images.length ? images : [cover],
        tags: form.tags,
        selling_points: form.selling_points.filter(Boolean),
        amenities: form.amenities,
        starting_price: parseFloat(form.starting_price || 0),
      };
      const { data: dev } = await api.post("/developments", payload);
      for (const u of form.units) {
        if (!u.price) continue;
        await api.post(`/developments/${dev.id}/units`, {
          label: u.label,
          floor: u.floor,
          bedrooms: u.bedrooms,
          price: parseFloat(u.price),
          deposit: parseFloat(u.deposit || u.price * 0.2),
          estimated_rent: parseFloat(u.estimated_rent || u.price * 0.007),
          status: "available",
        });
      }
      setCreatedDev(dev);
      setStep(6);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  const steps = ["Basics", "Units", "Payment Plan", "Media", "Review", "Documents"];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="add-development-wizard">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-fade-up">
        <div className="px-5 sm:px-8 py-4 sm:py-5 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-neutral-900">Add Development</h2>
            <p className="text-xs text-neutral-500">Step {step} of {steps.length} · {steps[step - 1]}</p>
          </div>
          <button data-testid="wizard-close" onClick={onClose} className="p-2 rounded-full hover:bg-neutral-100"><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div className="px-5 sm:px-8 pt-4 flex-shrink-0 overflow-x-auto">
          <div className="flex items-center gap-2">
            {steps.map((s, i) => {
              const idx = i + 1;
              const active = idx === step, done = idx < step;
              return (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-2 ${active ? "text-propera" : done ? "text-propera" : "text-neutral-400"}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-propera text-white" : done ? "bg-propera/20 text-propera" : "bg-neutral-100"}`}>
                      {done ? <Check size={12} /> : idx}
                    </div>
                    <span className="text-xs font-semibold hidden sm:inline">{s}</span>
                  </div>
                  {i < steps.length - 1 && <div className={`flex-1 h-px ${done ? "bg-propera" : "bg-neutral-200"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 sm:py-6">
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Development Basics</p>
              <Inp label="Development Name *" testId="w-name" value={form.name} onChange={(v) => set("name", v)} placeholder="e.g., Victoria Gardens" />
              <div>
                <label className="text-xs font-semibold text-neutral-700">Country *</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["Nigeria", "Ghana", "Kenya"].map((c) => (
                    <button
                      key={c}
                      data-testid={`w-country-${c}`}
                      type="button"
                      onClick={() => set("country", c)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition ${form.country === c ? "bg-propera text-white border-propera" : "bg-white border-neutral-200 hover:border-neutral-300"}`}
                    >{FLAGS[c]} {c}</button>
                  ))}
                </div>
              </div>
              <Inp label="City *" testId="w-city" value={form.city} onChange={(v) => set("city", v)} placeholder="e.g., Lagos" />
              <div>
                <label className="text-xs font-semibold text-neutral-700">Description *</label>
                <textarea data-testid="w-desc" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition" />
              </div>
              <Inp label="Estimated Completion Date" testId="w-completion" value={form.completion_date} onChange={(v) => set("completion_date", v)} placeholder="YYYY-MM-DD" />
              <Inp label="Starting Price (USD)" testId="w-price" type="number" value={form.starting_price} onChange={(v) => set("starting_price", v)} placeholder="85000" />
              <div>
                <label className="text-xs font-semibold text-neutral-700">Tags</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {TAGS.map((t) => (
                    <button key={t} type="button" data-testid={`w-tag-${t}`} onClick={() => toggleArr("tags", t)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${form.tags.includes(t) ? "bg-propera text-white" : "bg-neutral-100 text-neutral-700"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Unique Selling Points</label>
                {form.selling_points.map((sp, i) => (
                  <input key={i} data-testid={`w-sp-${i}`} value={sp} onChange={(e) => setSP(i, e.target.value)} placeholder="e.g., Waterfront views" className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition" />
                ))}
                <button type="button" onClick={addSellingPoint} className="mt-2 text-xs text-propera font-semibold hover:underline">+ Add point</button>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Amenities</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {AMENITIES.map((a) => (
                    <button key={a} type="button" data-testid={`w-amen-${a}`} onClick={() => toggleArr("amenities", a)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${form.amenities.includes(a) ? "bg-propera-light text-propera border-propera" : "bg-white border-neutral-200"}`}>{a}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Units</p>
              {form.units.map((u, i) => (
                <div key={i} className="soft-card p-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-center">
                  <input data-testid={`w-unit-label-${i}`} value={u.label} onChange={(e) => setUnit(i, "label", e.target.value)} placeholder="Label" className="px-3 py-2 rounded-lg border border-neutral-200 text-sm" />
                  <input value={u.floor} onChange={(e) => setUnit(i, "floor", e.target.value)} placeholder="Floor" className="px-3 py-2 rounded-lg border border-neutral-200 text-sm" />
                  <select value={u.bedrooms} onChange={(e) => setUnit(i, "bedrooms", e.target.value)} className="px-3 py-2 rounded-lg border border-neutral-200 text-sm">
                    <option>Studio</option><option>1-bed</option><option>2-bed</option><option>3-bed</option><option>4-bed</option>
                  </select>
                  <input data-testid={`w-unit-price-${i}`} value={u.price} onChange={(e) => setUnit(i, "price", e.target.value)} placeholder="Price" type="number" className="px-3 py-2 rounded-lg border border-neutral-200 text-sm" />
                  <input value={u.deposit} onChange={(e) => setUnit(i, "deposit", e.target.value)} placeholder="Deposit" type="number" className="px-3 py-2 rounded-lg border border-neutral-200 text-sm" />
                  <button type="button" onClick={() => removeUnit(i)} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
              ))}
              <button type="button" data-testid="w-add-unit" onClick={addUnit} className="text-xs text-propera font-semibold hover:underline">+ Add unit</button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Payment Plan</p>
              <p className="text-sm text-neutral-500">Allocate percentages to each milestone. Must total 100%.</p>
              <div className="grid grid-cols-3 gap-3">
                {["booking", "construction", "handover"].map((k) => (
                  <div key={k} className="soft-card p-5">
                    <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold capitalize">{k}</p>
                    <input
                      data-testid={`w-plan-${k}`}
                      type="number"
                      value={form.payment_plan[k]}
                      onChange={(e) => set("payment_plan", { ...form.payment_plan, [k]: parseFloat(e.target.value || 0) })}
                      className="mt-3 w-full px-3 py-2 rounded-lg border border-neutral-200 text-lg font-bold"
                    />
                    <p className="text-xs text-neutral-500 mt-1">%</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-500">Total: {form.payment_plan.booking + form.payment_plan.construction + form.payment_plan.handover}%</p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Media</p>
              <p className="text-sm text-neutral-500">Add up to 15 photos of the development. The first image is used as the cover on cards. Drag any tile to reorder.</p>
              <ImageUploader value={form.images} onChange={(imgs) => set("images", imgs)} max={15} testId="w-images" />
            </div>
          )}

          {step === 5 && (
            <div data-testid="w-review-step">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Review & Submit</p>
              <div className="soft-card p-5 mt-3 space-y-2 text-sm">
                <p><b>Name:</b> {form.name}</p>
                <p><b>Location:</b> {form.city}, {form.country}</p>
                <p><b>Starting price:</b> {fmtUSD(form.starting_price)}</p>
                <p><b>Tags:</b> {form.tags.join(", ") || "—"}</p>
                <p><b>Amenities:</b> {form.amenities.join(", ") || "—"}</p>
                <p><b>Units:</b> {form.units.filter((u) => u.price).length}</p>
              </div>
              <div className="rounded-2xl bg-propera-light/60 border border-propera/20 p-4 mt-4 text-sm text-neutral-700">
                <p className="font-semibold text-propera">Next: mandatory documents</p>
                <p className="mt-1">Saving creates your development as <b>Pending Documents</b>. You&apos;ll then upload the title, planning and building approvals required for {form.country}. Listings stay hidden from investors until Propera verifies every document.</p>
              </div>
            </div>
          )}

          {step === 6 && createdDev && (
            <div data-testid="w-documents-step">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">Mandatory Development Documents</p>
              <DevelopmentDocumentsPanel devId={createdDev.id} onSubmitted={() => setSubmitted(true)} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-8 py-4 border-t border-neutral-100 flex items-center justify-between flex-shrink-0">
          <button data-testid="wizard-back" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1 || step === 6} className="px-5 py-2.5 rounded-full text-sm font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
            <ArrowLeft size={14} /> Back
          </button>
          {step < 5 && (
            <button data-testid="wizard-next" onClick={() => setStep((s) => s + 1)} className="px-6 py-2.5 rounded-full bg-propera hover:bg-propera-hover text-white text-sm font-semibold transition flex items-center gap-1">
              Next <ArrowRight size={14} />
            </button>
          )}
          {step === 5 && (
            <button data-testid="wizard-submit" onClick={submit} disabled={submitting || !form.name || !form.city} className="px-6 py-2.5 rounded-full bg-propera hover:bg-propera-hover text-white text-sm font-semibold transition disabled:opacity-60">
              {submitting ? "Saving..." : "Save & add documents"}
            </button>
          )}
          {step === 6 && (
            <button data-testid="wizard-finish-later" onClick={onClose} className="px-6 py-2.5 rounded-full border border-neutral-200 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 transition-colors">
              Finish later
            </button>
          )}
        </div>
      </div>
      {submitted && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="wizard-submitted">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center animate-fade-up">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
              <Check size={26} />
            </div>
            <h3 className="text-2xl font-bold text-neutral-900">Submitted for review</h3>
            <p className="text-sm text-neutral-500 mt-2">Our team will review your development and supporting documents. You&apos;ll get an email as soon as it&apos;s approved and live on Propera.</p>
            <button data-testid="wizard-submitted-close" onClick={onClose} className="mt-6 w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition">
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-700">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition"
      />
    </div>
  );
}
